<script setup>
import { getActivePinia } from 'pinia';
import { onMounted, ref } from 'vue';

import { bestuurderStore } from '@/stores/bestuurder.js';
import { formStore } from '@/stores/form.js';
import { pakketStore } from '@/stores/pakket.js';
import { voertuigStore } from '@/stores/voertuig.js';

const form = formStore();
const pakket = pakketStore();
const voertuigen = voertuigStore();
const bestuurder = bestuurderStore();

const TheseOptions = ref(null);
const funnelType = ref('');
const berekenstraat = ref('regulier');
const sha = ref('');
const employee = ref('');
const licenseplate = ref('');
const postcode = ref('');
const huisnummer = ref('');
const toevoeging = ref('');

onMounted(() => {
  funnelType.value = form.funnelType;
  berekenstraat.value = pakket.berekenstraat;
  sha.value = form.sha;
  employee.value = form.medewerker;
  licenseplate.value = voertuigen.kenteken;
  postcode.value = bestuurder.postcode;
  huisnummer.value = bestuurder.huisnummer;
  toevoeging.value = bestuurder.toevoeging;
});

const setInitialValues = () => {
  form.setFunnelType(funnelType.value);
  voertuigen.setSoort(funnelType.value);
  pakket.setBerekenstraat(berekenstraat.value);
};

const getUrl = (queryVars = {}) => {
  let url = window.location.href;
  const params = new URLSearchParams(queryVars);
  url = url.split('?')[0];
  url = url.split('#')[0];
  return `${url}?${params.toString()}`;
};

const setFunnelType = (value) => {
  clearForm().then(() => {
    clearStore().then(() => {
      setInitialValues();
      form.setOverruleFunnelType(value);
      voertuigen.setSoort(value);
      pakket.setModule(value);
      window.open(getUrl({ dev: 1 }), '_SELF');
    });
  });
};

const setBerekenstraat = (value) => {
  clearForm().then(() => {
    clearStore().then(() => {
      setInitialValues();
      pakket.setBerekenstraat(value);
      window.open(getUrl({ dev: 1 }), '_SELF');
    });
  });
};

const openCalculation = (e) => {
  e.preventDefault();
  let queryVars = { dev: 1 };
  if (sha.value) queryVars['sha'] = encodeURIComponent(sha.value);
  if (employee.value) queryVars['medewerker'] = encodeURIComponent(employee.value);
  if (licenseplate.value) queryVars['prefill_kenteken'] = encodeURIComponent(licenseplate.value);
  if (postcode.value) queryVars['prefill_postcode'] = encodeURIComponent(postcode.value);
  if (huisnummer.value) queryVars['prefill_huisnummer'] = encodeURIComponent(huisnummer.value);
  if (toevoeging.value) queryVars['prefill_huisnummer_toevoegsel'] = encodeURIComponent(toevoeging.value);
  clearStore().then(() => {
    setInitialValues();
    window.open(getUrl(queryVars), '_SELF');
  });
};

const clearForm = async () => {
  sha.value = '';
  employee.value = '';
};

const clearStore = async () => {
  await sessionStorage.clear();
  const activeStores = Object.keys(getActivePinia().state.value);
  activeStores.forEach((store) => {
    getActivePinia()._s.get(store).$reset();
  });
};

const clearSessions = () => {
  clearForm().then(() => {
    clearStore().then(() => {
      window.open(getUrl({ dev: 1 }), '_SELF');
    });
  });
};
</script>

<template>
  <section class="bg-dark-subtle border-bottom p-3">
    <div class="container">
      <button
        class="btn btn-sm btn-secondary"
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#developmentOptions"
        aria-controls="developmentOptions"
        aria-label="Toggle development options"
      >
        {{ $t('devbar.developmentOptions') }}
      </button>
    </div>
  </section>

  <div
    id="developmentOptions"
    ref="TheseOptions"
    class="offcanvas offcanvas-start"
    tabindex="-1"
    aria-labelledby="developmentOptionsLabel"
  >
    <div class="offcanvas-header">
      <button
        type="button"
        class="btn btn-sm text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      >
        {{ $t('devbar.calculationClose') }}
      </button>
    </div>
    <div class="offcanvas-body">
      <div class="border-bottom py-5">
        <h5
          id="developmentOptionsLabel"
          class="offcanvas-title"
        >
          {{ $t('devbar.developmentOptions') }}
        </h5>
      </div>

      <div class="border-bottom py-5">
        <div class="fw-bold py-3">
          {{ $t('devbar.selectedFunnel') }}
        </div>
        <div class="form-check">
          <input
            id="funnel_auto"
            v-model="funnelType"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="auto"
            @click="setFunnelType('auto')"
          >
          <label
            class="form-check-label"
            for="funnel_auto"
          > {{ $t('devbar.selectFunnelOptionAuto') }} </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_bromfiets"
            v-model="funnelType"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="bromfiets"
            @click="setFunnelType('bromfiets')"
          >
          <label
            class="form-check-label"
            for="funnel_bromfiets"
          >
            {{ $t('devbar.selectFunnelOptionBromfiets') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_motorfiets"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="motorfiets"
            @click="setFunnelType('motorfiets')"
          >
          <label
            class="form-check-label"
            for="funnel_motorfiets"
          >
            {{ $t('devbar.selectFunnelOptionMotorfiets') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_invalidewagen"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="invalidewagen"
            @click="setFunnelType('invalidewagen')"
          >
          <label
            class="form-check-label"
            for="funnel_invalidewagen"
          >
            {{ $t('devbar.selectFunnelOptionInvalidewagen') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_herbouwwaarde"
            v-model="funnelType"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="herbouwwaarde"
            @click="setFunnelType('herbouwwaarde')"
          >
          <label
            class="form-check-label"
            for="funnel_herbouwwaarde"
          >
            {{ $t('devbar.selectFunnelOptionHerbouwwaarde') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_bedrijfspand"
            v-model="funnelType"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="bedrijfspand"
            @click="setFunnelType('bedrijfspand')"
          >
          <label
            class="form-check-label"
            for="funnel_bedrijfspand"
          >
            {{ $t('devbar.selectFunnelOptionBedrijfspand') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="funnel_voordeelpakket"
            v-model="funnelType"
            class="form-check-input"
            type="radio"
            name="funnelType"
            value="voordeelpakket"
            @click="setFunnelType('voordeelpakket')"
          >
          <label
            class="form-check-label"
            for="funnel_voordeelpakket"
          >
            {{ $t('devbar.selectFunnelOptionVoordeelpakket') }}
          </label>
        </div>
      </div>

      <div class="border-bottom py-5">
        <div class="fw-bold py-3">
          {{ $t('devbar.calculationType') }}
        </div>
        <div class="form-check">
          <input
            id="regulier"
            v-model="berekenstraat"
            class="form-check-input"
            type="radio"
            name="funnelBerekenstraat"
            value="regulier"
            @click="setBerekenstraat('regulier')"
          >
          <label
            class="form-check-label"
            for="regulier"
          > {{ $t('devbar.calculationTypeOptionRegulier') }} </label>
        </div>
        <div class="form-check">
          <input
            id="geennee"
            v-model="berekenstraat"
            class="form-check-input"
            type="radio"
            name="funnelBerekenstraat"
            value="geennee"
            @click="setBerekenstraat('geennee')"
          >
          <label
            class="form-check-label"
            for="geennee"
          > {{ $t('devbar.calculationTypeOptionGeennee') }} </label>
        </div>
      </div>

      <div class="border-bottom py-5">
        <a
          class="fw-bold py-3 d-block link-underline-light"
          data-bs-toggle="collapse"
          href="#collapseForm"
        >
          {{ $t('devbar.calculationOpenTitle') }}
        </a>
        <div
          id="collapseForm"
          class="collapse"
        >
          <form @submit="openCalculation">
            <div class="mb-3">
              <label
                for="sha"
                class="form-label col-form-label-sm"
              >{{ $t('devbar.sha') }}</label>
              <input
                id="sha"
                v-model="sha"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('devbar.sha')"
              >
            </div>
            <div class="mb-3">
              <label
                for="employee"
                class="form-label col-form-label-sm"
              >{{ $t('devbar.employee') }}</label>
              <input
                id="employee"
                v-model="employee"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('devbar.employee')"
              >
            </div>
            <hr>
            <div class="mb-3">
              <label
                for="licenseplate"
                class="form-label col-form-label-sm"
              >{{ $t('devbar.licenseplate') }}</label>
              <input
                id="licenseplate"
                v-model="licenseplate"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('devbar.licenseplate')"
              >
            </div>
            <div class="mb-3">
              <label
                for="postcode"
                class="form-label col-form-label-sm"
              >{{ $t('devbar.postcode') }}</label>
              <input
                id="postcode"
                v-model="postcode"
                type="text"
                class="form-control form-control-sm"
                placeholder="Postcode"
              >
              <div class="mb-3">
                <label
                  for="huisnummer"
                  class="form-label col-form-label-sm"
                >{{ $t('devbar.huisnummer') }}</label>
                <input
                  id="huisnummer"
                  v-model="huisnummer"
                  type="text"
                  class="form-control form-control-sm"
                  :placeholder="$t('devbar.huisnummer')"
                >
              </div>
              <div class="mb-3">
                <label
                  for="toevoeging"
                  class="form-label col-form-label-sm"
                >{{ $t('devbar.toevoeging') }}</label>
                <input
                  id="toevoeging"
                  v-model="toevoeging"
                  type="text"
                  class="form-control form-control-sm"
                  :placeholder="$t('devbar.toevoeging')"
                >
              </div>
            </div>
            <button
              type="submit"
              class="btn btn-primary"
            >
              {{ $t('devbar.calculationOpen') }}
            </button>
          </form>
        </div>
      </div>

      <div class="py-5 d-flex justify-content-end">
        <button
          class="btn btn-outline-danger"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#developmentOptions"
          aria-controls="reset"
          aria-expanded="false"
          aria-label="Reset the Pinia store"
          @click="clearSessions"
        >
          {{ $t('devbar.clearSession') }}
        </button>
      </div>
    </div>
  </div>
</template>
